import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Paper,
  TextField,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TablePagination,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import { CSVLink } from "react-csv";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosConfig";
import { exportToPDFQuestions } from "./utils/exportPdfQuestions";
import { authService } from "../../utils/authService";
import { useTranslation } from "react-i18next";

function ChecklistQuestionList() {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [serviceTypeFilter, setServiceTypeFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  const serviceTypeMapping = {
    preventivo: t("preventivo_ascensor"),
    correctivo: t("correctivo_ascensor"),
    instalacion: t("instalacion_ascensor"),
    electric_plant: t("preventivo_planta_electrica"),
    electric_plant_correctivo: t("correctivo_planta_electrica"),
    electric_plant_instalacion: t("instalacion_planta_electrica"),
    vehicular_door: t("preventivo_puerta_vehicular"),
    vehicular_door_correctivo: t("correctivo_puerta_vehicular"),
    vehicular_door_instalacion: t("instalacion_puerta_vehicular"),
    escalator: t("preventivo_escalera_electrica"),
    escalator_correctivo: t("correctivo_escalera_electrica"),
    escalator_instalacion: t("instalacion_escalera_electrica"),
  };

  const serviceTypeOptions = [
    { value: "all", label: t("allTypes") || "All" },
    { value: "preventivo", label: t("preventivo_ascensor") },
    { value: "correctivo", label: t("correctivo_ascensor") },
    { value: "instalacion", label: t("instalacion_ascensor") },
    { value: "electric_plant", label: t("preventivo_planta_electrica") },
    { value: "electric_plant_correctivo", label: t("correctivo_planta_electrica") },
    { value: "electric_plant_instalacion", label: t("instalacion_planta_electrica") },
    { value: "vehicular_door", label: t("preventivo_puerta_vehicular") },
    { value: "vehicular_door_correctivo", label: t("correctivo_puerta_vehicular") },
    { value: "vehicular_door_instalacion", label: t("instalacion_puerta_vehicular") },
    { value: "escalator", label: t("preventivo_escalera_electrica") },
    { value: "escalator_correctivo", label: t("correctivo_escalera_electrica") },
    { value: "escalator_instalacion", label: t("instalacion_escalera_electrica") },
  ];

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const token = authService.getToken();
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/checklist_questions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setQuestions(response.data);
    } catch (error) {
      console.error("Error fetching questions:", error);
    }
  };

  const handleStatusChange = async (id, active) => {
    try {
      const token = authService.getToken();
      await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/checklist_questions/${id}`,
        { checklist_question: { active: !active } },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchQuestions();
    } catch (error) {
      console.error("Error updating question status:", error);
    }
  };

  // Filtrado combinado por texto y por tipo de servicio
  const filteredData = useMemo(() => {
    return questions.filter((question) =>
      question.question.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (serviceTypeFilter === "all" ||
        question.service_type === serviceTypeFilter)
    );
  }, [questions, searchTerm, serviceTypeFilter]);

  // Paginación: determinar la porción de datos a mostrar
  const paginatedData = useMemo(() => {
    return filteredData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredData, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2} justifyContent="space-between" alignItems="center">
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            placeholder={t("searchQuestions")}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            select
            fullWidth
            label={t("filterByServiceType") || "Filter by Service Type"}
            variant="outlined"
            size="small"
            value={serviceTypeFilter}
            onChange={(e) => {
              setServiceTypeFilter(e.target.value);
              setPage(0);
            }}
          >
            {serviceTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} md={4} display="flex" gap={1} justifyContent="flex-end">
          <CSVLink data={filteredData} filename="questions.csv" style={{ textDecoration: "none" }}>
            <Button variant="outlined" startIcon={<DownloadIcon />}>
              {t("exportCSV")}
            </Button>
          </CSVLink>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => exportToPDFQuestions(filteredData)}
          >
            {t("exportPDF")}
          </Button>
        </Grid>

        <Grid item xs={12} md={4} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/questions-management")}
          >
            {t("createQuestion")}
          </Button>
        </Grid>
      </Grid>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("question")}</TableCell>
              <TableCell>{t("order")}</TableCell>
              <TableCell>{t("serviceType")}</TableCell>
              <TableCell>{t("inputType")}</TableCell>
              <TableCell>{t("status")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.question}</TableCell>
                <TableCell>{item.order}</TableCell>
                <TableCell>
                  {serviceTypeMapping[item.service_type] || item.service_type}
                </TableCell>
                <TableCell>{item.input_type}</TableCell>
                <TableCell>
                  <Switch
                    checked={item.active}
                    onChange={() => handleStatusChange(item.id, item.active)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </TableContainer>
    </Box>
  );
}

export default ChecklistQuestionList;
